import React, { useState } from 'react';
import { useContent } from '../lib/ContentContext';
import { assetUrl } from '../lib/api';
import { PageHero } from '../components/site/PageHero';
import { SectionHeading } from '../components/site/SectionHeading';
import { ImageLightbox } from '../components/site/ImageLightbox';

const getEmbedUrl = (url) => {
  if (!url) return '';
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

export default function About() {
  const { content } = useContent();
  const page = content?.about_page;
  const [lightboxIndex, setLightboxIndex] = useState(null);
  if (!page) return null;

  const infraImages = page.infrastructure_images || [];

  return (
    <div>
      <PageHero title={page.hero_title} subtitle={page.hero_subtitle} image={page.hero_image} />

      {/* Company Profile - text left, video right */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{page.profile_title}</SectionHeading>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="space-y-4">
              {(page.profile_paragraphs || []).map((p, i) => (
                <p key={i} className="cms-html text-black/80 leading-8" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
            <div>
              {page.profile_video_url ? (
                <div data-testid="about-profile-video" className="aspect-video rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(17,24,39,0.12)] bg-black">
                  <iframe
                    src={getEmbedUrl(page.profile_video_url)}
                    title="About Intexso Biochem"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]" data-testid="about-infrastructure-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{page.infrastructure_title || 'Infrastructure'}</SectionHeading>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
            {infraImages.map((img, i) => (
              <button
                key={i}
                type="button"
                data-testid="infrastructure-thumbnail"
                aria-label={`Enlarge infrastructure image ${i + 1}`}
                onClick={() => setLightboxIndex(i)}
                className="group relative aspect-[4/3] overflow-hidden rounded-xl shadow-[0_4px_14px_rgba(17,24,39,0.1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F4A300] focus-visible:ring-offset-2"
              >
                <img
                  src={assetUrl(img)}
                  alt={`Intexso infrastructure ${i + 1}`}
                  className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute inset-0 bg-[#26247B]/0 transition-colors duration-200 group-hover:bg-[#26247B]/15" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <ImageLightbox
        open={lightboxIndex !== null}
        onOpenChange={(o) => !o && setLightboxIndex(null)}
        src={lightboxIndex !== null ? assetUrl(infraImages[lightboxIndex]) : ''}
        alt={lightboxIndex !== null ? `Intexso infrastructure ${lightboxIndex + 1}` : ''}
      />

      {/* Mission Vision Values */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{page.mvv_title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {(page.mvv || []).map((item, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-[0_8px_20px_rgba(17,24,39,0.1)] p-8 text-center card-lift">
                <img src={assetUrl(item.image)} alt={item.title} className="h-20 w-20 object-contain mx-auto" loading="lazy" />
                <h3 className="mt-5 text-[#26247B] font-extrabold text-lg tracking-wide">{item.title}</h3>
                <p className="mt-3 text-black/80 text-sm leading-7">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={page.leadership_subtitle}>{page.leadership_title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10">
            {(page.leaders || []).map((leader, i) => (
              <div key={i} data-testid="about-leader-card" className="bg-white rounded-2xl border border-[#E6E8EE] shadow-[0_8px_20px_rgba(17,24,39,0.06)] p-7 flex flex-col sm:flex-row gap-6">
                <img
                  src={assetUrl(leader.image)}
                  alt={leader.name}
                  className="h-32 w-32 rounded-xl object-cover shrink-0 mx-auto sm:mx-0"
                  loading="lazy"
                />
                <div>
                  <h3 className="text-[#26247B] font-extrabold text-lg">{leader.name}</h3>
                  <p className="text-[#2E8B57] font-bold text-sm mt-1">{leader.role}</p>
                  <p className="mt-3 text-black/80 text-sm leading-6">{leader.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={page.certifications_text}>{page.certifications_title}</SectionHeading>
          <div data-testid="about-certifications-scroller" className="mt-10 marquee max-w-4xl mx-auto">
            <div className="marquee-track gap-8 pr-8">
              {[...(page.certification_images || []), ...(page.certification_images || [])].map((img, i) => (
                <div key={i} className="bg-white rounded-xl border border-[#E6E8EE] p-4 shadow-[0_4px_12px_rgba(17,24,39,0.06)] shrink-0 mr-8">
                  <img src={assetUrl(img)} alt={`Certification ${(i % (page.certification_images || []).length) + 1}`} className="h-[138px] w-auto object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{page.milestones_title}</SectionHeading>
          <div className="mt-10">
            <img src={assetUrl(page.milestones_image)} alt="Intexso milestones timeline" className="w-full rounded-2xl" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{page.clients_title}</SectionHeading>
          <div data-testid="about-clients-scroller" className="mt-10 marquee">
            <div className="marquee-track marquee-slow gap-5 pr-5">
              {[...(page.client_logos || []), ...(page.client_logos || [])].map((logo, i) => (
                <div key={i} className="bg-white rounded-xl border border-[#E6E8EE] p-4 flex items-center justify-center h-28 w-44 shrink-0 mr-5">
                  <img src={assetUrl(logo)} alt={`Client ${(i % (page.client_logos || []).length) + 1}`} className="max-h-20 max-w-full w-auto object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
